import { h, type ComponentChild } from 'preact';

type ChatTextPart = {
  text: string;
  italic: boolean;
};

const WORD_CHAR_RE = /[\p{L}\p{N}]/u;
const URL_RE = /(https?:\/\/[^\s<>"']+)/gi;

function isWordChar(ch: string): boolean {
  if (!ch) return false;
  return WORD_CHAR_RE.test(ch);
}

function parseChatTextParts(content: string): ChatTextPart[] {
  const text = String(content || '');
  if (!text) return [{ text: '', italic: false }];

  const parts: ChatTextPart[] = [];
  let cursor = 0;

  while (cursor < text.length) {
    const open = text.indexOf('*', cursor);
    if (open < 0) break;

    const close = text.indexOf('*', open + 1);
    if (close < 0) break;

    const inner = text.slice(open + 1, close);
    const prev = open > 0 ? text[open - 1] : '';
    const next = close + 1 < text.length ? text[close + 1] : '';
    const looksSafeEmphasis = (
      inner.length >= 2 &&
      inner.length <= 280 &&
      !inner.includes('*') &&
      !inner.includes('\n') &&
      !isWordChar(prev) &&
      !isWordChar(next)
    );

    if (!looksSafeEmphasis) {
      cursor = open + 1;
      continue;
    }

    if (open > cursor) parts.push({ text: text.slice(cursor, open), italic: false });
    parts.push({ text: inner, italic: true });
    cursor = close + 1;
  }

  if (!parts.length) return [{ text, italic: false }];
  if (cursor < text.length) parts.push({ text: text.slice(cursor), italic: false });
  return parts;
}

function splitTrailingPunctuation(url: string): { href: string; trailing: string } {
  let href = String(url || '');
  let trailing = '';
  while (href.length > 0 && /[.,!?;:)\]}]/.test(href[href.length - 1])) {
    trailing = href[href.length - 1] + trailing;
    href = href.slice(0, -1);
  }
  return { href, trailing };
}

function renderPlainText(text: string, keyPrefix: string) {
  const value = String(text || '');
  if (!value) return null;
  const nodes: ComponentChild[] = [];
  let cursor = 0;
  URL_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = URL_RE.exec(value)) !== null) {
    const raw = match[0] || '';
    const start = match.index;
    if (start > cursor) {
      nodes.push(<span key={`${keyPrefix}_t_${cursor}`}>{value.slice(cursor, start)}</span>);
    }
    const { href, trailing } = splitTrailingPunctuation(raw);
    if (href) {
      nodes.push(
        <a
          key={`${keyPrefix}_a_${start}`}
          class="chat-inline-link"
          href={href}
          target="_blank"
          rel="noreferrer"
        >
          {href}
        </a>
      );
    }
    if (trailing) nodes.push(<span key={`${keyPrefix}_p_${start}`}>{trailing}</span>);
    cursor = start + raw.length;
  }
  if (cursor < value.length) nodes.push(<span key={`${keyPrefix}_t_${cursor}`}>{value.slice(cursor)}</span>);
  return nodes.length ? nodes : value;
}

export function FormattedChatText(props: { content: string }) {
  const parts = parseChatTextParts(props.content);
  return (
    <span>
      {parts.map((part, idx) => (
        part.italic
          ? <em key={idx} class="chat-inline-emphasis">{part.text}</em>
          : <span key={idx}>{renderPlainText(part.text, `p_${idx}`)}</span>
      ))}
    </span>
  );
}
